
import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { Store, CartItem, Product } from './types';
import { supabase } from './lib/supabaseClient';
import { User } from '@supabase/supabase-js';

interface AppContextType {
  stores: Store[];
  currentStore: Store | null;
  isLoading: boolean;
  user: User | null;
  error: string | null;
  setCurrentStoreBySlug: (slug: string) => Promise<void>;
  fetchStores: () => Promise<void>;
  cart: CartItem[];
  addToCart: (product: Product) => void;
  removeFromCart: (productId: string) => void;
  updateQuantity: (productId: string, delta: number) => void;
  clearCart: () => void;
  signOut: () => Promise<void>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

const ADMIN_EMAIL = 'contatomurillocunha@gmail.com';

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [stores, setStores] = useState<Store[]>([]);
  const [currentStore, setCurrentStore] = useState<Store | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Inicializa o carrinho do localStorage
  const [cart, setCart] = useState<CartItem[]>(() => {
    const savedCart = localStorage.getItem('localcommerce_cart');
    return savedCart ? JSON.parse(savedCart) : [];
  });

  // Persiste o carrinho sempre que mudar
  useEffect(() => {
    localStorage.setItem('localcommerce_cart', JSON.stringify(cart));
  }, [cart]);

  // Recupera a loja atual do localStorage se estiver no checkout ou admin
  useEffect(() => {
    const savedStoreSlug = localStorage.getItem('localcommerce_last_slug');
    const hash = window.location.hash;
    if (savedStoreSlug && !currentStore && (hash.includes('checkout') || hash.includes('admin'))) {
      setCurrentStoreBySlug(savedStoreSlug);
    }
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchStores = async () => {
    if (!user) return;
    setIsLoading(true);
    setError(null);
    
    try {
      let query = supabase.from('stores').select('*');
      
      if (user.email !== ADMIN_EMAIL) {
        query = query.eq('user_id', user.id);
      }
      
      const { data, error: supabaseError } = await query.order('name');
      
      if (supabaseError) throw supabaseError;
      setStores(data || []);
    } catch (err: any) {
      console.error("Erro na busca de lojas:", err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setStores([]);
    setUser(null);
  };

  const setCurrentStoreBySlug = async (slug: string) => {
    if (currentStore?.slug === slug) return;
    
    setIsLoading(true);
    setError(null);

    try {
      const { data: store, error: storeError } = await supabase
        .from('stores')
        .select('*')
        .eq('slug', slug)
        .single();

      if (storeError) throw storeError;

      const { data: products, error: prodError } = await supabase
        .from('products')
        .select('*')
        .eq('store_id', store.id)
        .eq('active', true)
        .order('category', { ascending: true });

      if (prodError) throw prodError;

      const fullStore = { ...store, products: products || [] };
      setCurrentStore(fullStore);
      localStorage.setItem('localcommerce_last_slug', slug);
    } catch (err: any) {
      console.error("Erro ao buscar loja por slug:", err);
      setError("Loja não encontrada.");
      setCurrentStore(null);
    } finally {
      setIsLoading(false);
    }
  };

  const addToCart = (product: Product) => {
    setCart(prev => {
      const existing = prev.find(item => item.product.id === product.id);
      if (existing) {
        return prev.map(item =>
          item.product.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { product, quantity: 1 }];
    });
  };

  const removeFromCart = (productId: string) => {
    setCart(prev => prev.filter(item => item.product.id !== productId));
  };

  const updateQuantity = (productId: string, delta: number) => {
    setCart(prev => prev.map(item => {
      if (item.product.id === productId) {
        const newQty = Math.max(1, item.quantity + delta);
        return { ...item, quantity: newQty };
      }
      return item;
    }));
  };

  const clearCart = () => {
    setCart([]);
    localStorage.removeItem('localcommerce_cart');
  };

  return (
    <AppContext.Provider value={{
      stores,
      currentStore,
      isLoading,
      user,
      error,
      setCurrentStoreBySlug,
      fetchStores,
      cart,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      signOut
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error('useApp must be used within AppProvider');
  return context;
};
